/**
 * NepaliCalendar - Nepali (BS) calendar implementation
 */

import CalendarBase from "./CalendarBase.js";
import NepaliDateConverter from "../converters/NepaliDateConverter.js";

class NepaliCalendar extends CalendarBase {
  constructor(options = {}) {
    super(options);
    this.converter = new NepaliDateConverter();
  }

  /**
   * Get current date in BS
   */
  getCurrentDate() {
    return this.converter.getTodayBS();
  }

  /**
   * Get month names based on language
   */
  getMonthNames() {
    return this.options.language === "ne"
      ? this.converter.getNepaliMonths()
      : this.converter.getEnglishMonths();
  }

  /**
   * Get weekday names based on language
   */
  getWeekdayNames() {
    return this.options.language === "ne"
      ? this.converter.getNepaliWeekdays()
      : this.converter.getEnglishWeekdays();
  }

  /**
   * Get days in month
   */
  getDaysInMonth(year, month) {
    if (
      !this.converter.bsMonthDays[year] ||
      !this.converter.bsMonthDays[year][month - 1]
    ) {
      return 30; // Default fallback
    }
    return this.converter.bsMonthDays[year][month - 1];
  }

  /**
   * Get first day of month (0-6, where 0 is Sunday)
   */
  getFirstDayOfMonth(year, month) {
    const date = { year, month, day: 1 };
    return this.converter.getDayOfWeek(date);
  }

  /**
   * Format date
   */
  formatDate(date, format = "YYYY-MM-DD") {
    return this.converter.formatBsDate(date, format);
  }

  /**
   * Get day of week for a date (0-6, where 0 is Sunday)
   * @protected
   */
  _getDayOfWeek(date) {
    return this.converter.getDayOfWeek(date);
  }

  /**
   * Create days grid
   * @protected
   */
  _createDaysGrid() {
    const daysGrid = document.createElement("div");
    daysGrid.className = "ndtp-days-grid";

    const year = this.viewDate.year;
    const month = this.viewDate.month;
    const daysInMonth = this.getDaysInMonth(year, month);
    const firstDayOfMonth = this.getFirstDayOfMonth(year, month);

    // Adjust for week start
    let startOffset = firstDayOfMonth - this.options.weekStart;
    if (startOffset < 0) startOffset += 7;

    // Previous month days
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const daysInPrevMonth = this.getDaysInMonth(prevYear, prevMonth);

    for (let i = startOffset - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const date = { year: prevYear, month: prevMonth, day };
      const cell = this._createDayCell(date, false);
      daysGrid.appendChild(cell);
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = { year, month, day };
      const cell = this._createDayCell(date, true);
      daysGrid.appendChild(cell);
    }

    // Next month days
    const totalCells = daysGrid.children.length;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);

    if (remainingCells > 0) {
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;

      for (let day = 1; day <= remainingCells; day++) {
        const date = { year: nextYear, month: nextMonth, day };
        const cell = this._createDayCell(date, false);
        daysGrid.appendChild(cell);
      }
    }

    return daysGrid;
  }

  /**
   * Create day cell with Nepali digits if language is 'ne'
   * @protected
   */
  _createDayCell(date, isCurrentMonth = true) {
    const cell = super._createDayCell(date, isCurrentMonth);

    if (this.options.language === "ne") {
      cell.textContent = this.converter.toNepaliDigits(date.day);
    }

    return cell;
  }

  /**
   * Create year select with Nepali digits if language is 'ne'
   * @protected
   */
  _createYearSelect() {
    const select = document.createElement("select");
    select.className = "ndtp-year-select";

    const currentYear = this.viewDate.year;
    const minYear = this.converter.minBsYear;
    const maxYear = this.converter.maxBsYear;

    for (let year = minYear; year <= maxYear; year++) {
      const option = document.createElement("option");
      option.value = year;
      option.textContent =
        this.options.language === "ne"
          ? this.converter.toNepaliDigits(year)
          : year;

      if (year === currentYear) {
        option.selected = true;
      }
      select.appendChild(option);
    }

    select.onchange = (e) => {
      this.viewDate.year = parseInt(e.target.value, 10);
      this._render();
      this._triggerChange();
    };

    return select;
  }

  /**
   * Convert BS date to AD date
   */
  toAd(bsDate) {
    return this.converter.bsToAd(bsDate);
  }

  /**
   * Convert AD date to BS date
   */
  toBs(adDate) {
    return this.converter.adToBs(adDate);
  }

  /**
   * Get formatted date string
   */
  getFormattedDate(date, format = "YYYY-MM-DD") {
    const formattedDate = this.formatDate(date, format);

    if (this.options.language === "ne") {
      // Convert to Nepali digits
      return this.converter.toNepaliDigits(formattedDate);
    }

    return formattedDate;
  }

  /**
   * Parse date string to date object
   */
  parseDate(dateString, format = "YYYY-MM-DD") {
    // If in Nepali, convert digits first
    if (this.options.language === "ne") {
      dateString = dateString.replace(/[०-९]/g, (digit) => {
        const nepaliDigits = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
        return nepaliDigits.indexOf(digit);
      });
    }

    // Parse based on format
    let year, month, day;

    if (format === "YYYY-MM-DD") {
      const parts = dateString.split("-");
      if (parts.length !== 3) return null;

      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    } else if (format === "YYYY/MM/DD") {
      const parts = dateString.split("/");
      if (parts.length !== 3) return null;

      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    } else if (format === "DD-MM-YYYY") {
      const parts = dateString.split("-");
      if (parts.length !== 3) return null;

      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    } else if (format === "DD/MM/YYYY") {
      const parts = dateString.split("/");
      if (parts.length !== 3) return null;

      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    } else if (format === "MM-DD-YYYY") {
      const parts = dateString.split("-");
      if (parts.length !== 3) return null;

      month = parseInt(parts[0], 10);
      day = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    } else if (format === "MM/DD/YYYY") {
      const parts = dateString.split("/");
      if (parts.length !== 3) return null;

      month = parseInt(parts[0], 10);
      day = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
    } else {
      return null;
    }

    // Validate the parsed values
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
    }

    if (year < this.converter.minBsYear || year > this.converter.maxBsYear) {
      return null;
    }

    if (month < 1 || month > 12) {
      return null;
    }

    const daysInMonth = this.getDaysInMonth(year, month);
    if (day < 1 || day > daysInMonth) {
      return null;
    }

    return { year, month, day };
  }

  /**
   * Check if a date is valid
   */
  isValidDate(date) {
    if (!date || typeof date !== "object") {
      return false;
    }

    const { year, month, day } = date;

    if (
      !Number.isInteger(year) ||
      !Number.isInteger(month) ||
      !Number.isInteger(day)
    ) {
      return false;
    }

    if (year < this.converter.minBsYear || year > this.converter.maxBsYear) {
      return false;
    }

    if (month < 1 || month > 12) {
      return false;
    }

    const daysInMonth = this.getDaysInMonth(year, month);
    if (day < 1 || day > daysInMonth) {
      return false;
    }

    return true;
  }

  /**
   * Add days to a date
   */
  addDays(date, days) {
    if (!this.isValidDate(date)) {
      throw new Error("Invalid date");
    }

    // Convert to AD, add days, convert back to BS
    const adDate = this.converter.bsToAd(date);
    const jsDate = new Date(adDate.year, adDate.month - 1, adDate.day);
    jsDate.setDate(jsDate.getDate() + days);

    const newAdDate = {
      year: jsDate.getFullYear(),
      month: jsDate.getMonth() + 1,
      day: jsDate.getDate(),
    };

    return this.converter.adToBs(newAdDate);
  }

  /**
   * Add months to a date
   */
  addMonths(date, months) {
    if (!this.isValidDate(date)) {
      throw new Error("Invalid date");
    }

    let year = date.year;
    let month = date.month + months;
    let day = date.day;

    // Adjust year and month
    while (month > 12) {
      month -= 12;
      year++;
    }
    while (month < 1) {
      month += 12;
      year--;
    }

    // Adjust day if it exceeds the days in the new month
    const daysInNewMonth = this.getDaysInMonth(year, month);
    if (day > daysInNewMonth) {
      day = daysInNewMonth;
    }

    return { year, month, day };
  }

  /**
   * Add years to a date
   */
  addYears(date, years) {
    if (!this.isValidDate(date)) {
      throw new Error("Invalid date");
    }

    const newYear = date.year + years;
    let day = date.day;

    // Check if the new year is valid
    if (
      newYear < this.converter.minBsYear ||
      newYear > this.converter.maxBsYear
    ) {
      throw new Error("Resulting year is out of range");
    }

    // Adjust day if it exceeds the days in the new year/month
    const daysInNewMonth = this.getDaysInMonth(newYear, date.month);
    if (day > daysInNewMonth) {
      day = daysInNewMonth;
    }

    return { year: newYear, month: date.month, day };
  }

  /**
   * Get the difference between two dates in days
   */
  getDaysDifference(date1, date2) {
    if (!this.isValidDate(date1) || !this.isValidDate(date2)) {
      throw new Error("Invalid date");
    }

    const adDate1 = this.converter.bsToAd(date1);
    const adDate2 = this.converter.bsToAd(date2);

    const jsDate1 = new Date(adDate1.year, adDate1.month - 1, adDate1.day);
    const jsDate2 = new Date(adDate2.year, adDate2.month - 1, adDate2.day);

    const diffTime = jsDate2.getTime() - jsDate1.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Get start of week for a given date
   */
  getStartOfWeek(date) {
    if (!this.isValidDate(date)) {
      throw new Error("Invalid date");
    }

    const dayOfWeek = this._getDayOfWeek(date);
    const daysToSubtract = (dayOfWeek - this.options.weekStart + 7) % 7;

    return this.addDays(date, -daysToSubtract);
  }

  /**
   * Get end of week for a given date
   */
  getEndOfWeek(date) {
    if (!this.isValidDate(date)) {
      throw new Error("Invalid date");
    }

    const dayOfWeek = this._getDayOfWeek(date);
    const daysToAdd = (6 - dayOfWeek + this.options.weekStart) % 7;

    return this.addDays(date, daysToAdd);
  }

  /**
   * Get start of month
   */
  getStartOfMonth(date) {
    if (!this.isValidDate(date)) {
      throw new Error("Invalid date");
    }

    return { year: date.year, month: date.month, day: 1 };
  }

  /**
   * Get end of month
   */
  getEndOfMonth(date) {
    if (!this.isValidDate(date)) {
      throw new Error("Invalid date");
    }

    const daysInMonth = this.getDaysInMonth(date.year, date.month);
    return { year: date.year, month: date.month, day: daysInMonth };
  }

  /**
   * Get start of year
   */
  getStartOfYear(date) {
    if (!this.isValidDate(date)) {
      throw new Error("Invalid date");
    }

    return { year: date.year, month: 1, day: 1 };
  }

  /**
   * Get end of year
   */
  getEndOfYear(date) {
    if (!this.isValidDate(date)) {
      throw new Error("Invalid date");
    }

    const daysInLastMonth = this.getDaysInMonth(date.year, 12);
    return { year: date.year, month: 12, day: daysInLastMonth };
  }
}

export default NepaliCalendar;
